/**
 * @param {Function} hitAxios
 * @returns {Promise<{
 *   use_per_second: boolean;
 *   credits_per_second: number;
 *   stitching_flat: number;
 *   models: Record<string, { mode: string; rate: number; credits_per_second: number; credits_flat: number }>;
 * }>}
 */
export async function fetchVideoPricing(hitAxios) {
  const res = await hitAxios({
    path: "/api/video-studio/pricing",
    post: false,
    showLoading: false,
    showSnackbar: false,
  });
  if (!res?.data?.success) {
    return {
      use_per_second: false,
      credits_per_second: 0,
      stitching_flat: 0,
      models: {},
    };
  }
  const d = res.data.data || {};
  return {
    use_per_second: Boolean(d.use_per_second),
    credits_per_second: Number(d.credits_per_second) || 0,
    stitching_flat: Number(d.stitching_flat) || 0,
    models: d.models && typeof d.models === "object" ? d.models : {},
  };
}

function modelPricingEntry(pricing, uiModelId) {
  const id = String(uiModelId || "").trim();
  if (!id || !pricing?.models) return null;
  return pricing.models[id] || null;
}

function isFlatModelPricing(model) {
  if (!model) return false;
  const perSec = Number(model.credits_per_second) || 0;
  const flat = Number(model.credits_flat) || 0;
  if (String(model.mode || "") === "flat") return flat > 0;
  return flat > 0 && perSec <= 0;
}

/**
 * Estimate credits for one clip / total duration using env-backed pricing.
 * Prefers per-model rates from `pricing.models[uiModelId]`.
 * Does not fall back to the global per-second rate when a model entry exists
 * (that caused wrong totals after switching models).
 *
 * @param {number} durationSec
 * @param {{
 *   use_per_second?: boolean;
 *   credits_per_second?: number;
 *   models?: Record<string, { credits_per_second?: number; credits_flat?: number; mode?: string }>;
 * }} pricing
 * @param {string} [uiModelId]
 * @returns {number | null}
 */
export function estimateCreditsFromPricing(durationSec, pricing, uiModelId = "") {
  const d = Math.max(1, Math.round(Number(durationSec) || 5));
  const model = modelPricingEntry(pricing, uiModelId);

  if (model) {
    if (isFlatModelPricing(model)) {
      return Math.max(1, Math.round(Number(model.credits_flat) || 0));
    }
    const perSec = Number(model.credits_per_second) || 0;
    if (perSec > 0) return Math.max(1, Math.round(d * perSec));
    const flat = Number(model.credits_flat) || 0;
    if (flat > 0) return Math.max(1, Math.round(flat));
    return null;
  }

  // Unknown model id — only then use the global fallback rate.
  if (!pricing?.use_per_second || !pricing.credits_per_second) return null;
  return Math.max(1, Math.round(d * pricing.credits_per_second));
}

/**
 * Full job estimate for the current form (target duration × model rate, or flat × segments).
 *
 * @param {object} form
 * @param {object} pricing
 * @param {{
 *   willStitch?: boolean;
 *   segmentCount?: number;
 *   fallback?: (durationSec: number) => number;
 * }} [opts]
 * @returns {number}
 */
export function estimateJobCreditsFromPricing(form, pricing, opts = {}) {
  const targetSec = Math.max(1, Math.round(Number(form?.duration) || 0));
  const modelId = String(form?.model || "").trim();
  const willStitch = Boolean(opts.willStitch);
  const segmentCount = Math.max(1, Math.round(Number(opts.segmentCount) || 1));
  const stitchFlat = willStitch ? Number(pricing?.stitching_flat) || 0 : 0;
  const model = modelPricingEntry(pricing, modelId);

  if (model && isFlatModelPricing(model)) {
    const flat = Math.max(1, Math.round(Number(model.credits_flat) || 0));
    const gens = willStitch ? segmentCount : 1;
    return Math.max(1, flat * gens + stitchFlat);
  }

  const fromEnv = estimateCreditsFromPricing(targetSec, pricing, modelId);
  if (fromEnv != null) return Math.max(1, fromEnv + stitchFlat);

  const fallback = typeof opts.fallback === "function" ? opts.fallback(targetSec) : 0;
  return Math.max(1, (Number(fallback) || 0) + stitchFlat);
}
